import React from 'react';

interface PrivacyPolicyProps {
  onBack: () => void;
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  return (
    <div className="bg-slate-50 min-h-full font-sans">
      <nav className="bg-white border-b border-slate-100 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 cursor-pointer" onClick={onBack}>
                <img 
                    src="https://image2url.com/r2/default/images/1769369928623-3180bbac-a140-4e50-ac88-1cfcf218a72a.png" 
                    alt="LoveOnRead Logo" 
                    className="w-8 h-8 object-contain"
                />
                <span className="font-bold text-xl tracking-tight">
                    <span className="text-love">Love</span>
                    <span className="text-read">OnRead</span>
                </span>
            </div>
            <button 
                onClick={onBack}
                className="text-sm font-medium text-slate-600 hover:text-slate-900"
            >
                Back
            </button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-extrabold text-slate-900 mb-8">Privacy Policy</h1>
        <div className="prose prose-slate max-w-none bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
            <p className="text-sm text-slate-500 mb-6">Last Updated: February 2026</p>

            <h3>1. Introduction</h3>
            <p>
                Welcome to LoveOnRead ("we," "our," or "us"). We respect your privacy and are committed to protecting the personal data you share with us. This policy explains how we collect, use, and safeguard your information when you use our <strong>website, mobile application,</strong> and AI coaching services.
            </p>

            <h3>2. Information We Collect</h3>
            <ul>
                <li><strong>Account Information:</strong> We collect your email address, authentication credentials, and a unique user ID to create your account, sync your subscription status across devices, and manage your preferences.</li>
                <li><strong>Usage Data:</strong> We log anonymous interaction data, including session duration, feature usage, and subscription status, to improve the quality of our services.</li>
                <li><strong>Conversation Data:</strong> To provide AI coaching, your text inputs are processed by our AI provider (<strong>Google Gemini</strong>). These inputs are used solely to generate coaching responses and relationship simulations in real-time.</li>
                <li><strong>Purchase History:</strong> We track your subscription status (e.g., "Pro" vs "Free"). Depending on your platform, payments are processed by either <strong>Paystack</strong> (Web) or the <strong>Apple App Store</strong> (Mobile). We <strong>do not</strong> store your actual credit card details or financial information on our servers.</li>
            </ul>

            <h3>3. How We Use Your Data</h3>
            <p>We use your information for the following purposes:</p>
            <ul>
                <li>To provide, operate, and maintain the Service.</li>
                <li>To validate your active subscription via <strong>Paystack</strong> (Web) or <strong>RevenueCat</strong> (Mobile).</li>
                <li>To generate AI relationship advice and roleplay scenarios.</li>
                <li>To prevent fraud and ensure the security of our platform.</li>
            </ul>

            <h3>4. Data Sharing and Third Parties</h3>
            <p>
                We do not sell your personal data to advertisers. We share data only with trusted third-party service providers necessary to operate the App:
            </p>
            <ul>
                <li><strong>Google Firebase:</strong> For secure user authentication, database storage, and cloud infrastructure.</li>
                <li><strong>Google Gemini API:</strong> For processing the text content of your chats to generate AI responses.</li>
                <li><strong>Paystack:</strong> To process secure payments on our Website.</li>
                <li><strong>RevenueCat & Apple App Store:</strong> To manage and validate subscriptions on our Mobile App.</li>
            </ul>

            <h3>5. Data Retention & Deletion</h3>
            <p>
                We retain your personal information only for as long as is necessary to provide our services to you.
            </p>
            <p><strong>Account Deletion:</strong> You have the right to delete your account and all associated data at any time.</p>
            <ul>
                <li><strong>In-App / Web:</strong> Go to <strong>Settings &gt; Delete Account</strong> to permanently remove your data.</li>
                <li><strong>By Request:</strong> You may also contact us at <a href="mailto:loveonreadai@gmail.com">loveonreadai@gmail.com</a> to request data deletion.</li>
            </ul>
            <p>Once an account is deleted, your chat history, user profile, and progression data are permanently removed from our servers.</p>

            <h3>6. Data Security</h3>
            <p>
                We implement industry-standard security measures, including encryption in transit (HTTPS/SSL) and at rest, to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h3>7. Children’s Privacy</h3>
            <p>
                Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If you are a parent or guardian and you are aware that your child has provided us with Personal Data, please contact us.
            </p>

            <h3>8. Changes to This Policy</h3>
            <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>

            <h3>9. Contact Us</h3>
            <p>
                If you have any questions about this Privacy Policy, please contact us at <a href="mailto:loveonreadai@gmail.com">loveonreadai@gmail.com</a>.
            </p>
        </div>
      </main>
    </div>
  );
};